import React, { useState, useEffect } from 'react';
import { X, Save, Tag, Loader2, Palette, Edit2, Trash2, Plus, FolderOpen } from 'lucide-react';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const CATEGORY_COLORS = [
    { value: 'red', bg: 'bg-red-500', label: 'Red' },
    { value: 'orange', bg: 'bg-orange-500', label: 'Orange' },
    { value: 'amber', bg: 'bg-amber-500', label: 'Amber' },
    { value: 'yellow', bg: 'bg-yellow-500', label: 'Yellow' },
    { value: 'lime', bg: 'bg-lime-500', label: 'Lime' },
    { value: 'green', bg: 'bg-green-500', label: 'Green' },
    { value: 'emerald', bg: 'bg-emerald-500', label: 'Emerald' },
    { value: 'teal', bg: 'bg-teal-500', label: 'Teal' },
    { value: 'cyan', bg: 'bg-cyan-500', label: 'Cyan' },
    { value: 'sky', bg: 'bg-sky-500', label: 'Sky' },
    { value: 'blue', bg: 'bg-blue-500', label: 'Blue' },
    { value: 'indigo', bg: 'bg-indigo-500', label: 'Indigo' },
    { value: 'violet', bg: 'bg-violet-500', label: 'Violet' },
    { value: 'purple', bg: 'bg-purple-500', label: 'Purple' },
    { value: 'fuchsia', bg: 'bg-fuchsia-500', label: 'Fuchsia' },
    { value: 'pink', bg: 'bg-pink-500', label: 'Pink' },
];

const CategoryModal = ({
    categories = [],
    onCreateCategory,
    onUpdateCategory,
    onDeleteCategory,
    onClose,
    isSaving,
}) => {
    const [mode, setMode] = useState('list'); // 'list' | 'add' | 'edit'
    const [editingCategory, setEditingCategory] = useState(null);
    const [name, setName] = useState('');
    const [color, setColor] = useState('blue');
    const [description, setDescription] = useState('');
    const [error, setError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        if (mode === 'edit' && editingCategory) {
            setName(editingCategory.name || '');
            setColor(editingCategory.color || 'blue');
            setDescription(editingCategory.description || '');
        } else if (mode === 'add') {
            setName('');
            setColor('blue');
            setDescription('');
        }
    }, [mode, editingCategory]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!name.trim()) {
            setError('Category name is required');
            return;
        }

        try {
            if (mode === 'edit' && editingCategory) {
                await onUpdateCategory(editingCategory.id, { name, color, description });
            } else {
                await onCreateCategory({ name, color, description });
            }
            setMode('list');
            setEditingCategory(null);
        } catch (err) {
            setError(err.message || 'Failed to save category');
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setMode('edit');
    };

    const handleDelete = async (categoryId) => {
        try {
            await onDeleteCategory(categoryId);
            setDeleteConfirm(null);
        } catch (err) {
            setError(err.message || 'Failed to delete category');
        }
    };

    const getColorClass = (colorValue) => {
        return CATEGORY_COLORS.find(c => c.value === colorValue)?.bg || 'bg-blue-500';
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg max-h-[80vh] flex flex-col bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#ff4d4d]/10 rounded-lg">
                            <FolderOpen className="w-5 h-5 text-[#ff4d4d]" />
                        </div>
                        <h2 className="text-xl font-bold text-white">
                            {mode === 'list' ? 'Categories' : mode === 'add' ? 'New Category' : 'Edit Category'}
                        </h2>
                    </div>
                    <button
                        onClick={mode === 'list' ? onClose : () => setMode('list')}
                        className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                {mode === 'list' ? (
                    <>
                        {/* Categories List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {categories.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="p-4 bg-white/5 rounded-full mb-4">
                                        <Tag size={32} className="text-white/30" />
                                    </div>
                                    <p className="text-white/50 text-sm mb-4">No categories yet</p>
                                    <button
                                        onClick={() => setMode('add')}
                                        className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition"
                                    >
                                        <Plus size={18} />
                                        Add Category
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {categories.map(category => (
                                        <div
                                            key={category.id}
                                            className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/10 rounded-xl hover:border-white/20 transition"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-4 h-4 rounded-full ${getColorClass(category.color)}`} />
                                                <div>
                                                    <p className="font-medium text-white">{category.name}</p>
                                                    {category.description && (
                                                        <p className="text-sm text-white/50">{category.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-sm text-white/40 mr-2">
                                                    {category.product_count || 0} products
                                                </span>
                                                <button
                                                    onClick={() => handleEdit(category)}
                                                    className="p-2 text-white/40 hover:text-sky-400 hover:bg-sky-400/10 rounded-lg transition"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(category)}
                                                    className="p-2 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Add Button */}
                        {categories.length > 0 && (
                            <div className="p-4 border-t border-white/10">
                                <button
                                    onClick={() => setMode('add')}
                                    className="w-full px-4 py-2.5 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center justify-center gap-2 transition"
                                >
                                    <Plus size={18} />
                                    Add Category
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Add/Edit Form */}
                        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Category Name *
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                                    placeholder="e.g., Electronics, Clothing"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Color
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {CATEGORY_COLORS.map(c => (
                                        <button
                                            key={c.value}
                                            type="button"
                                            onClick={() => setColor(c.value)}
                                            className={`w-8 h-8 rounded-lg transition-all ${c.bg} ${
                                                color === c.value
                                                    ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0d1424] scale-110'
                                                    : 'hover:scale-105'
                                            }`}
                                            title={c.label}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Description (optional)
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition resize-none"
                                    placeholder="Brief description of this category..."
                                />
                            </div>

                            {error && (
                                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                                    {error}
                                </div>
                            )}
                        </form>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-[#0a0f1c]">
                            <button
                                type="button"
                                onClick={() => setMode('list')}
                                className="px-4 py-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSaving}
                                className="px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        {mode === 'edit' ? 'Update' : 'Create'}
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <ConfirmDeleteModal
                    isOpen={true}
                    onConfirm={() => handleDelete(deleteConfirm.id)}
                    onCancel={() => setDeleteConfirm(null)}
                    title="Delete Category"
                    message={`Are you sure you want to delete "${deleteConfirm.name}"? Products in this category will be uncategorized.`}
                />
            )}
        </div>
    );
};

export default CategoryModal;
